import React from "react";

/**
 * Reusable hint system for both Single Player and Multiplayer modes
 * Handles hint logic and rendering consistently across both game modes
 */

/**
 * Calculate hint parameters for a word
 * @param {string} word - The word to calculate hints for
 * @param {number} currentHintLength - Current number of letters revealed
 * @returns {object} Hint calculation results
 */
export const calculateHintParams = (word, currentHintLength = 0) => {
  const maxHintLength = Math.floor(word.length / 2) + 1;
  const canGiveMoreHints = currentHintLength < maxHintLength;

  return {
    maxHintLength,
    canGiveMoreHints,
    currentHintLength,
  };
};

/**
 * Process a wrong guess and determine hint actions
 * @param {number} wrongAttemptsForHint - Current wrong attempts counter
 * @param {string} word - The word being guessed
 * @param {number} currentHintLength - Current hint length for this word
 * @returns {object} Hint action results
 */
export const processWrongGuess = (
  wrongAttemptsForHint,
  word,
  currentHintLength = 0
) => {
  const { canGiveMoreHints } = calculateHintParams(word, currentHintLength);

  // Check if it's time for a hint (every 3 wrong attempts)
  if (wrongAttemptsForHint >= 3) {
    if (canGiveMoreHints) {
      return {
        shouldGiveHint: true,
        newHintLength: currentHintLength + 1,
        resetCounter: true,
        message: "",
      };
    } else {
      return {
        shouldGiveHint: false,
        newHintLength: currentHintLength,
        resetCounter: false,
        message: "No more hints allowed for this word!",
      };
    }
  } else {
    // Show countdown message
    if (!canGiveMoreHints) {
      return {
        shouldGiveHint: false,
        newHintLength: currentHintLength,
        resetCounter: false,
        message: "No more hints allowed for this word!",
      };
    }

    const guessesUntilHint = 3 - wrongAttemptsForHint;
    const message =
      guessesUntilHint === 1
        ? "1 more guess until a hint"
        : `${guessesUntilHint} more guesses until a hint`;

    return {
      shouldGiveHint: false,
      newHintLength: currentHintLength,
      resetCounter: false,
      message,
    };
  }
};

/**
 * Render a word with hints applied
 * @param {string} word - The word to render
 * @param {number} hintLength - Number of letters to reveal
 * @param {boolean} isGameOver - Whether the game is over (show all letters)
 * @returns {JSX.Element} Rendered word with hints
 */
export const renderWordWithHints = (
  word,
  hintLength = 0,
  isGameOver = false
) => {
  if (isGameOver) {
    return word.split("").join(" ");
  }

  return word.split("").map((letter, letterIndex) => {
    if (letterIndex < hintLength) {
      return (
        <span key={letterIndex} className="hint-letter">
          {letter}
        </span>
      );
    }
    return letterIndex === word.length - 1 ? "_" : "_ ";
  });
};

/**
 * Hint countdown display component
 * @param {string} message - The hint countdown message
 * @returns {JSX.Element} Hint countdown display
 */
export const HintCountdown = ({ message }) => {
  return (
    <div
      className="hint-countdown-container"
      style={{ minHeight: "2rem", margin: "1rem 0", textAlign: "center" }}>
      {message && (
        <div
          className={`hint-countdown ${
            message.includes("No more hints") ? "no-more-hints" : ""
          }`}>
          {message}
        </div>
      )}
    </div>
  );
};

/**
 * Word chain display component with consistent hint rendering
 * @param {Array} guessedWords - Array of correctly guessed words
 * @param {Array} remainingWords - Array of remaining words to guess
 * @param {object} wordHints - Object mapping word indices to hint lengths
 * @param {number} currentProgress - Current progress index
 * @param {number} animatingWordIndex - Index of word currently animating
 * @param {boolean} isGameOver - Whether the game is over
 * @returns {JSX.Element} Word chain display
 */
export const WordChain = ({
  guessedWords,
  remainingWords,
  wordHints,
  currentProgress,
  animatingWordIndex = -1,
  isGameOver = false,
}) => {
  return (
    <div className="word-chain">
      {/* Render guessed words */}
      {guessedWords.map((word, index) => (
        <span
          key={index}
          className={`word ${
            index === animatingWordIndex ? "word-falling" : ""
          }`}>
          {word}
        </span>
      ))}

      {/* Render remaining words with hints */}
      {remainingWords.map((word, index) => {
        const actualWordIndex = currentProgress + 1 + index;
        const hintLength = wordHints[String(actualWordIndex)] || 0;
        const isCurrentGuess = index === 0; // First remaining word is the current guess

        return (
          <span 
            key={actualWordIndex} 
            className={`word missing-letters ${isCurrentGuess ? 'current-guess' : ''}`}
          >
            {renderWordWithHints(word, hintLength, isGameOver)}
          </span>
        );
      })}
    </div>
  );
};

const HintSystem = {
  calculateHintParams,
  processWrongGuess,
  renderWordWithHints,
  HintCountdown,
  WordChain,
};

export default HintSystem;
